import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Type, Hash, ToggleLeft, List, ChevronDown } from 'lucide-react';
import Icon from '../../../shared/components/Icon';
import axiosInstance from '../../../shared/services/api/axiosInstance';

// Icon for object property type: renders "{}"
const ObjectIcon = ({ className }) => (
  <span className={className} style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 'inherit' }}>{'{}'}</span>
);

// Shared cache for property discovery (used by component and preload); TTL 3 min
const PROPERTIES_CACHE = new Map();
const RAW_PRELOAD_CACHE = new Map(); // raw API responses from preload
const CACHE_TTL_MS = 3 * 60 * 1000;

/**
 * Event Property Filter Module
 * 
 * Opens when Filter icon is clicked next to an event.
 * Allows filtering events by their properties (predefined + custom) and user properties.
 * 
 * Features:
 * - Left navigation: All, Events, Users
 * - Right panel: Properties based on selected nav
 * - Filter builder with WHERE/AND/OR logic
 * - Data type icons for each property
 * - Operators based on data type
 */
const EventPropertyFilterModule = ({
  isOpen,
  onClose,
  triggerRef,
  eventName,
  eventId,
  teamId,
  filters = [],
  onFiltersChange,
  existingFilter = null,
  firstTimeOnly = false,
  onFirstTimeChange,
  isGlobalFilter = false,
  onGlobalFilterAdd = null
}) => {
  const [selectedNavItem, setSelectedNavItem] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef(null);
  const nestedPopupRef = useRef(null);

  // Property discovery
  const [eventProperties, setEventProperties] = useState([]);
  const [userProperties, setUserProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);

  // Use shared cache (same as preloadPropertyDiscovery)

  // Filter builder state
  const [propertyFilters, setPropertyFilters] = useState([]);

  // First time only: local state for toggle, synced from parent on open
  const [firstTimeOnlyChecked, setFirstTimeOnlyChecked] = useState(firstTimeOnly);

  // Nested property selector state (for object properties)
  const [showNestedSelector, setShowNestedSelector] = useState(false);
  const [selectedObjectProperty, setSelectedObjectProperty] = useState(null);
  const [nestedPosition, setNestedPosition] = useState({ top: 0, left: 0 });

  // Calculate position relative to trigger element with viewport bounds checking.
  // When triggerRef is missing (e.g. edge cases while editing), fall back to
  // centering the popup in the viewport so it never appears at (0, 0).
  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;

    const popupWidth = 600; // Approximate width (minWidth: 550px, maxWidth: 700px)
    const popupHeight = 400; // Approximate height
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 16; // Minimum margin from viewport edges

    let top;
    let left;

    if (triggerRef?.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();

      // Calculate initial position (below trigger, aligned to left)
      top = triggerRect.bottom + scrollY + 8;
      left = triggerRect.left + scrollX;

      // Check if popup would overflow right edge
      if (left + popupWidth > viewportWidth - margin) {
        // Position to the left of trigger instead
        left = triggerRect.right + scrollX - popupWidth;
        // If still overflows, align to right edge with margin
        if (left < margin) {
          left = viewportWidth - popupWidth - margin;
        }
      }

      // Check if popup would overflow left edge
      if (left < margin) {
        left = margin;
      }

      // Check if popup would overflow bottom edge
      if (top + popupHeight > viewportHeight + scrollY - margin) {
        // Position above trigger instead
        top = triggerRect.top + scrollY - popupHeight - 8;
        // If still overflows, align to top with margin
        if (top < scrollY + margin) {
          top = scrollY + margin;
        }
      }
    } else {
      // Fallback: center the popup in the viewport
      left = scrollX + (viewportWidth - popupWidth) / 2;
      top = scrollY + (viewportHeight - popupHeight) / 2;
    }

    // Ensure popup never goes above top margin
    if (top < scrollY + margin) {
      top = scrollY + margin;
    }

    setPosition({ top, left });
  }, [isOpen, triggerRef]);

  // Position nested property popup when it opens (after main popup has laid out)
  useEffect(() => {
    if (showNestedSelector && selectedObjectProperty && popupRef.current) {
      const rect = popupRef.current.getBoundingClientRect();
      setNestedPosition({
        top: rect.top,
        left: rect.right + 8
      });
    }
  }, [showNestedSelector, selectedObjectProperty]);

  // Sync first-time toggle from parent when popup opens (must be before any early return)
  useEffect(() => {
    if (isOpen) setFirstTimeOnlyChecked(firstTimeOnly);
  }, [isOpen, firstTimeOnly]);

  // When popup opens: show predefined immediately, then fetch custom in background (or global: predefined event + user + user custom only)
  useEffect(() => {
    if (isOpen && teamId && (eventName || isGlobalFilter)) {
      if (isGlobalFilter) {
        const predefinedEventProps = getPredefinedEventProperties();
        const predefinedUserProps = getUserProperties();
        setEventProperties(predefinedEventProps); // Global: no custom event properties
        setUserProperties(predefinedUserProps);
        fetchPropertiesForGlobal(predefinedUserProps);
      } else {
        const cacheKey = `${teamId}-${eventName}`;
        PROPERTIES_CACHE.delete(cacheKey);
        RAW_PRELOAD_CACHE.delete(cacheKey);
        const predefinedEventProps = getPredefinedEventProperties();
        const predefinedUserProps = getUserProperties();
        setEventProperties(predefinedEventProps);
        setUserProperties(predefinedUserProps);
        fetchProperties(predefinedEventProps, predefinedUserProps);
      }
    }
  }, [isOpen, eventName, teamId, isGlobalFilter]);

  const fetchProperties = async (predefinedProps, predefinedUserProps) => {
    if (!teamId || !eventName) return;

    const cacheKey = `${teamId}-${eventName}`;
    const cached = PROPERTIES_CACHE.get(cacheKey);
    if (cached && cached.timestamp && (Date.now() - cached.timestamp) < CACHE_TTL_MS) {
      setEventProperties(cached.eventProperties);
      setUserProperties(cached.userProperties);
      return;
    }

    // Check raw preload cache (populated by preloadPropertyDiscovery from Events page)
    const rawCached = RAW_PRELOAD_CACHE.get(cacheKey);
    if (rawCached && rawCached.timestamp && (Date.now() - rawCached.timestamp) < CACHE_TTL_MS) {
      const customProps = [];
      if (rawCached.customPropsResponse?.data?.success) {
        (rawCached.customPropsResponse.data.custom_properties || []).forEach(prop => {
          let propertyType = prop.type || 'string';
          let listItemType = propertyType.startsWith('list<') && propertyType.endsWith('>') ? propertyType.slice(5, -1) : null;
          if (listItemType) propertyType = 'list';
          customProps.push({ 
            name: prop.name, 
            label: prop.name, 
            type: propertyType, 
            listItemType, 
            source: 'event', 
            isCustom: true, 
            sampleValues: prop.sampleValues || [],
            nestedProperties: prop.nestedProperties || undefined
          });
        });
        (rawCached.customPropsResponse.data.auto_event_data || []).forEach(prop => {
          let propertyType = prop.type || 'string';
          let listItemType = propertyType.startsWith('list<') && propertyType.endsWith('>') ? propertyType.slice(5, -1) : null;
          if (listItemType) propertyType = 'list';
          customProps.push({ 
            name: `auto_event_data.${prop.name}`, 
            label: `Auto Event: ${prop.name}`, 
            type: propertyType, 
            listItemType, 
            source: 'event', 
            isCustom: false, 
            sampleValues: prop.sampleValues || [],
            nestedProperties: prop.nestedProperties || undefined
          });
        });
      }
      const userCustomProps = [];
      if (rawCached.userCustomPropsResponse?.data?.success && rawCached.userCustomPropsResponse.data.custom_properties) {
        rawCached.userCustomPropsResponse.data.custom_properties.forEach(prop => {
          let propertyType = prop.type || 'string';
          let listItemType = propertyType.startsWith('list<') && propertyType.endsWith('>') ? propertyType.slice(5, -1) : null;
          if (listItemType) propertyType = 'list';
          userCustomProps.push({ 
            name: `user.${prop.name}`, 
            label: prop.name, 
            type: propertyType, 
            listItemType, 
            source: 'user', 
            isCustom: true, 
            sampleValues: prop.sampleValues || [],
            nestedProperties: prop.nestedProperties || undefined
          });
        });
      }
      const finalEventProperties = [...(predefinedProps || getPredefinedEventProperties()), ...customProps];
      const finalUserProperties = [...(predefinedUserProps || getUserProperties()), ...userCustomProps];
      setEventProperties(finalEventProperties);
      setUserProperties(finalUserProperties);
      PROPERTIES_CACHE.set(cacheKey, { eventProperties: finalEventProperties, userProperties: finalUserProperties, timestamp: Date.now() });
      RAW_PRELOAD_CACHE.delete(cacheKey);
      setLoadingProperties(false);
      return;
    }

    setLoadingProperties(true);
    try {
      let numericTeamId = null;
      if (typeof teamId === 'object' && teamId !== null) {
        numericTeamId = parseInt(teamId._id || teamId.id, 10);
      } else if (typeof teamId === 'string') {
        try {
          const teamDataStr = localStorage.getItem('selectedTeamData');
          if (teamDataStr) {
            const teamData = JSON.parse(teamDataStr);
            numericTeamId = parseInt(teamData._id || teamData.id, 10);
          } else {
            numericTeamId = parseInt(teamId, 10);
          }
        } catch (e) {
          numericTeamId = parseInt(teamId, 10);
        }
      } else {
        numericTeamId = parseInt(teamId, 10);
      }

      if (isNaN(numericTeamId) || !numericTeamId) {
        setLoadingProperties(false);
        return;
      }

      const [customPropsResponse, userCustomPropsResponse] = await Promise.all([
        axiosInstance.get('/events/custom-properties', {
          params: { team_id: numericTeamId, event_name: eventName, limit: 200 }
        }),
        axiosInstance.get('/events/user-custom-properties', {
          params: { team_id: numericTeamId, limit: 300 }
        })
      ]);

      const predefinedEvent = predefinedProps || getPredefinedEventProperties();
      const predefinedUser = predefinedUserProps || getUserProperties();
      const customProps = [];
      if (customPropsResponse.data.success) {
        if (customPropsResponse.data.custom_properties) {
          customPropsResponse.data.custom_properties.forEach(prop => {
            let propertyType = prop.type || 'string';
            let listItemType = null;
            if (propertyType.startsWith('list<') && propertyType.endsWith('>')) {
              listItemType = propertyType.slice(5, -1);
              propertyType = 'list';
            }
            customProps.push({
              name: prop.name,
              label: prop.name,
              type: propertyType,
              listItemType: listItemType,
              source: 'event',
              isCustom: true,
              sampleValues: prop.sampleValues || [],
              nestedProperties: prop.nestedProperties || undefined
            });
          });
        }
        if (customPropsResponse.data.auto_event_data) {
          customPropsResponse.data.auto_event_data.forEach(prop => {
            let propertyType = prop.type || 'string';
            let listItemType = null;
            if (propertyType.startsWith('list<') && propertyType.endsWith('>')) {
              listItemType = propertyType.slice(5, -1);
              propertyType = 'list';
            }
            customProps.push({
              name: `auto_event_data.${prop.name}`,
              label: `Auto Event: ${prop.name}`,
              type: propertyType,
              listItemType: listItemType,
              source: 'event',
              isCustom: false,
              sampleValues: prop.sampleValues || [],
              nestedProperties: prop.nestedProperties || undefined
            });
          });
        }
      }

      const userCustomProps = [];
      if (userCustomPropsResponse.data.success && userCustomPropsResponse.data.custom_properties) {
        userCustomPropsResponse.data.custom_properties.forEach(prop => {
          let propertyType = prop.type || 'string';
          let listItemType = null;
          if (propertyType.startsWith('list<') && propertyType.endsWith('>')) {
            listItemType = propertyType.slice(5, -1);
            propertyType = 'list';
          }
          userCustomProps.push({
            name: `user.${prop.name}`,
            label: prop.name,
            type: propertyType,
            listItemType: listItemType,
            source: 'user',
            isCustom: true,
            sampleValues: prop.sampleValues || [],
            nestedProperties: prop.nestedProperties || undefined
          });
        });
      }

      const finalEventProperties = [...predefinedEvent, ...customProps];
      const finalUserProperties = [...predefinedUser, ...userCustomProps];
      setEventProperties(finalEventProperties);
      setUserProperties(finalUserProperties);
      PROPERTIES_CACHE.set(cacheKey, {
        eventProperties: finalEventProperties,
        userProperties: finalUserProperties,
        timestamp: Date.now()
      });
    } catch (error) {
      console.error('Error fetching properties:', error);
      if (predefinedProps && predefinedUserProps) {
        setEventProperties(predefinedProps);
        setUserProperties(predefinedUserProps);
      }
    } finally {
      setLoadingProperties(false);
    }
  };

  // Global filter mode: only predefined event + predefined user + user custom (no event custom)
  const fetchPropertiesForGlobal = async (predefinedUserProps) => {
    if (!teamId) return;
    setLoadingProperties(true);
    try {
      let numericTeamId = typeof teamId === 'object' && teamId !== null
        ? parseInt(teamId._id || teamId.id, 10)
        : parseInt(teamId, 10);
      if (typeof teamId === 'string' && isNaN(numericTeamId)) {
        try {
          const teamDataStr = localStorage.getItem('selectedTeamData');
          if (teamDataStr) {
            const teamData = JSON.parse(teamDataStr);
            numericTeamId = parseInt(teamData._id || teamData.id, 10);
          }
        } catch (e) {
          numericTeamId = parseInt(teamId, 10);
        }
      }
      if (isNaN(numericTeamId) || !numericTeamId) {
        setLoadingProperties(false);
        return;
      }
      const userCustomPropsResponse = await axiosInstance.get('/events/user-custom-properties', {
        params: { team_id: numericTeamId, limit: 5000 }
      });
      const userCustomProps = [];
      if (userCustomPropsResponse.data?.success && userCustomPropsResponse.data.custom_properties) {
        userCustomPropsResponse.data.custom_properties.forEach(prop => {
          let propertyType = prop.type || 'string';
          let listItemType = propertyType.startsWith('list<') && propertyType.endsWith('>') ? propertyType.slice(5, -1) : null;
          if (listItemType) propertyType = 'list';
          userCustomProps.push({
            name: `user.${prop.name}`,
            label: prop.name,
            type: propertyType,
            listItemType,
            source: 'user',
            isCustom: true,
            sampleValues: prop.sampleValues || [],
            nestedProperties: prop.nestedProperties || undefined
          });
        });
      }
      const finalUserProperties = [...(predefinedUserProps || getUserProperties()), ...userCustomProps];
      setUserProperties(finalUserProperties);
    } catch (error) {
      console.error('Error fetching user properties for global filter:', error);
      if (predefinedUserProps) setUserProperties(predefinedUserProps);
    } finally {
      setLoadingProperties(false);
    }
  };

  // Get predefined event properties
  const getPredefinedEventProperties = () => {
    return [
      // Core event properties (removed: event_name, event_type, event_category, timestamp, session_id, user_id)
      { name: 'distinct_id', label: 'Distinct ID', type: 'string', source: 'event' },
      
      // User Identity
      { name: 'user_identity', label: 'User Identity', type: 'boolean', source: 'event', special: 'is_identified' },
      
      // Location properties
      { name: 'country', label: 'Country', type: 'string', source: 'event' },
      { name: 'city', label: 'City', type: 'string', source: 'event' },
      { name: 'region', label: 'Region', type: 'string', source: 'event' },
      { name: 'timezone', label: 'Timezone', type: 'string', source: 'event' },
      
      // Device & Browser properties
      { name: 'browser_name', label: 'Browser Name', type: 'string', source: 'event' },
      { name: 'browser_version', label: 'Browser Version', type: 'string', source: 'event' },
      { name: 'os', label: 'Operating System', type: 'string', source: 'event' },
      { name: 'os_version', label: 'OS Version', type: 'string', source: 'event' },
      { name: 'device_type', label: 'Device Type', type: 'string', source: 'event' },
      { name: 'language', label: 'Language', type: 'string', source: 'event' },
      
      // Page & URL properties
      { name: 'current_url', label: 'Current URL', type: 'string', source: 'event' },
      { name: 'page_title', label: 'Page Title', type: 'string', source: 'event' },
      { name: 'uri', label: 'URI', type: 'string', source: 'event' },
      { name: 'initial_referrer', label: 'Initial Referrer', type: 'string', source: 'event' },
      { name: 'referrer', label: 'Referrer', type: 'string', source: 'event' },
      
      // UTM parameters
      { name: 'utm_source', label: 'UTM Source', type: 'string', source: 'event' },
      { name: 'utm_medium', label: 'UTM Medium', type: 'string', source: 'event' },
      { name: 'utm_campaign', label: 'UTM Campaign', type: 'string', source: 'event' },
      { name: 'utm_term', label: 'UTM Term', type: 'string', source: 'event' },
      { name: 'utm_content', label: 'UTM Content', type: 'string', source: 'event' },
      { name: 'utm_id', label: 'UTM ID', type: 'string', source: 'event' },
      
      // Element properties
      { name: 'element_name', label: 'Element Name', type: 'string', source: 'event' },
      { name: 'element_id', label: 'Element ID', type: 'string', source: 'event' },
      { name: 'element_class', label: 'Element Class', type: 'string', source: 'event' },
      { name: 'element_tag_name', label: 'Element Tag Name', type: 'string', source: 'event' },
      { name: 'element_type', label: 'Element Type', type: 'string', source: 'event' },
      { name: 'element_text', label: 'Element Text', type: 'string', source: 'event' },
      
      // Viewport & Screen properties (numbers)
      { name: 'viewport_height', label: 'Viewport Height', type: 'number', source: 'event' },
      { name: 'viewport_width', label: 'Viewport Width', type: 'number', source: 'event' },
      { name: 'screen_height', label: 'Screen Height', type: 'number', source: 'event' },
      { name: 'screen_width', label: 'Screen Width', type: 'number', source: 'event' },
      { name: 'dpi', label: 'DPI', type: 'number', source: 'event' },
      
      // Web3 properties
      { name: 'is_web3_user', label: 'Is Web3 User', type: 'boolean', source: 'event' },
      { name: 'wallet_address', label: 'Wallet Address', type: 'boolean', source: 'event', special: 'exists' }
    ];
  };

  // Discover event properties from a sample event (unused - keeping for reference)
  const discoverEventProperties = (eventRow) => {
    const properties = [];

    // Predefined event properties - Complete list
    const predefinedProps = [
      // Core event properties (removed: event_name, event_type, event_category, timestamp, session_id, user_id)
      { name: 'distinct_id', label: 'Distinct ID', type: 'string', source: 'event' },
      
      // User Identity
      { name: 'user_identity', label: 'User Identity', type: 'boolean', source: 'event', special: 'is_identified' },
      
      // Location properties
      { name: 'country', label: 'Country', type: 'string', source: 'event' },
      { name: 'city', label: 'City', type: 'string', source: 'event' },
      { name: 'region', label: 'Region', type: 'string', source: 'event' },
      { name: 'timezone', label: 'Timezone', type: 'string', source: 'event' },
      
      // Device & Browser properties
      { name: 'browser_name', label: 'Browser Name', type: 'string', source: 'event' },
      { name: 'browser_version', label: 'Browser Version', type: 'string', source: 'event' },
      { name: 'os', label: 'Operating System', type: 'string', source: 'event' },
      { name: 'os_version', label: 'OS Version', type: 'string', source: 'event' },
      { name: 'device_type', label: 'Device Type', type: 'string', source: 'event' },
      { name: 'language', label: 'Language', type: 'string', source: 'event' },
      
      // Page & URL properties
      { name: 'current_url', label: 'Current URL', type: 'string', source: 'event' },
      { name: 'page_title', label: 'Page Title', type: 'string', source: 'event' },
      { name: 'uri', label: 'URI', type: 'string', source: 'event' },
      { name: 'initial_referrer', label: 'Initial Referrer', type: 'string', source: 'event' },
      { name: 'referrer', label: 'Referrer', type: 'string', source: 'event' },
      
      // UTM parameters
      { name: 'utm_source', label: 'UTM Source', type: 'string', source: 'event' },
      { name: 'utm_medium', label: 'UTM Medium', type: 'string', source: 'event' },
      { name: 'utm_campaign', label: 'UTM Campaign', type: 'string', source: 'event' },
      { name: 'utm_term', label: 'UTM Term', type: 'string', source: 'event' },
      { name: 'utm_content', label: 'UTM Content', type: 'string', source: 'event' },
      { name: 'utm_id', label: 'UTM ID', type: 'string', source: 'event' },
      
      // Element properties
      { name: 'element_name', label: 'Element Name', type: 'string', source: 'event' },
      { name: 'element_id', label: 'Element ID', type: 'string', source: 'event' },
      { name: 'element_class', label: 'Element Class', type: 'string', source: 'event' },
      { name: 'element_tag_name', label: 'Element Tag Name', type: 'string', source: 'event' },
      { name: 'element_type', label: 'Element Type', type: 'string', source: 'event' },
      { name: 'element_text', label: 'Element Text', type: 'string', source: 'event' },
      
      // Viewport & Screen properties (numbers)
      { name: 'viewport_height', label: 'Viewport Height', type: 'number', source: 'event' },
      { name: 'viewport_width', label: 'Viewport Width', type: 'number', source: 'event' },
      { name: 'screen_height', label: 'Screen Height', type: 'number', source: 'event' },
      { name: 'screen_width', label: 'Screen Width', type: 'number', source: 'event' },
      { name: 'dpi', label: 'DPI', type: 'number', source: 'event' },
      
      // Web3 properties
      { name: 'is_web3_user', label: 'Is Web3 User', type: 'boolean', source: 'event' },
      { name: 'wallet_address', label: 'Wallet Address', type: 'boolean', source: 'event', special: 'exists' }
    ];

    predefinedProps.forEach(prop => {
      if (eventRow[prop.name] !== undefined && eventRow[prop.name] !== null) {
        properties.push(prop);
      }
    });

    // Custom properties (from custom_properties JSONB)
    if (eventRow.custom_properties) {
      let customProps = {};
      if (typeof eventRow.custom_properties === 'string') {
        try {
          customProps = JSON.parse(eventRow.custom_properties);
        } catch (e) {
          console.warn('Failed to parse custom_properties:', e);
        }
      } else if (typeof eventRow.custom_properties === 'object') {
        customProps = eventRow.custom_properties;
      }

      Object.keys(customProps).forEach(key => {
        const value = customProps[key];
        const dataType = inferDataType(value);
        properties.push({
          name: key,
          label: key,
          type: dataType,
          source: 'event',
          isCustom: true
        });
      });
    }

    // Auto event data properties
    if (eventRow.auto_event_data) {
      let autoData = {};
      if (typeof eventRow.auto_event_data === 'string') {
        try {
          autoData = JSON.parse(eventRow.auto_event_data);
        } catch (e) {
          console.warn('Failed to parse auto_event_data:', e);
        }
      } else if (typeof eventRow.auto_event_data === 'object') {
        autoData = eventRow.auto_event_data;
      }

      Object.keys(autoData).forEach(key => {
        const value = autoData[key];
        const dataType = inferDataType(value);
        properties.push({
          name: `auto_event_data.${key}`,
          label: `Auto Event: ${key}`,
          type: dataType,
          source: 'event',
          isCustom: false
        });
      });
    }

    return properties;
  };

  // Get user properties for user identities - Complete list
  const getUserProperties = () => {
    const userProperties = [
      // Boolean properties
      { name: 'user.is_identified', label: 'Is Identified', type: 'boolean', source: 'user' },
      { name: 'user.is_bounce', label: 'Is Bounce', type: 'boolean', source: 'user' },
      { name: 'user.is_web3_user', label: 'Is Web3 User', type: 'boolean', source: 'user' },
      { name: 'user.is_wallet_connected', label: 'Is Wallet Connected', type: 'boolean', source: 'user' },
      { name: 'user.transacted_user', label: 'Transacted User', type: 'boolean', source: 'user' },
      
      // Date/Time properties
      { name: 'user.first_visit', label: 'First Visit', type: 'datetime', source: 'user' },
      { name: 'user.last_visit', label: 'Last Visit', type: 'datetime', source: 'user' },
      
      // Number properties
      { name: 'user.total_page_views', label: 'Total Page Views', type: 'number', source: 'user' },
      { name: 'user.total_time_spent', label: 'Total Time Spent', type: 'number', source: 'user' },
      
      // String properties
      { name: 'user.first_referrer', label: 'First Referrer', type: 'string', source: 'user' },
      { name: 'user.last_referrer', label: 'Last Referrer', type: 'string', source: 'user' },
      { name: 'user.first_location_city', label: 'First Location City', type: 'string', source: 'user' },
      { name: 'user.first_location_country', label: 'First Location Country', type: 'string', source: 'user' },
      { name: 'user.last_location_city', label: 'Last Location City', type: 'string', source: 'user' },
      { name: 'user.last_location_country', label: 'Last Location Country', type: 'string', source: 'user' },
      { name: 'user.device_type', label: 'Device Type', type: 'string', source: 'user' },
      { name: 'user.browser_name', label: 'Browser Name', type: 'string', source: 'user' },
      { name: 'user.os', label: 'Operating System', type: 'string', source: 'user' }
    ];

    // Custom user properties will be discovered dynamically from user_identities table
    // This can be added later with a similar API endpoint

    return userProperties;
  };

  // Infer data type from value
  const inferDataType = (value) => {
    if (value === null || value === undefined) return 'string';
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    if (value instanceof Date || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value))) return 'datetime';
    if (Array.isArray(value)) return 'list';
    if (typeof value === 'object') return 'object';
    return 'string';
  };

  // Get data type icon
  const getDataTypeIcon = (dataType) => {
    switch (dataType) {
      case 'string':
        return Type;
      case 'number':
        return Hash;
      case 'datetime':
        return ({ color = "currentColor", size = 18 }) => <Icon name="calendar" size={size} color={color} />;
      case 'boolean':
        return ToggleLeft;
      case 'list':
        return List;
      case 'object':
        return ObjectIcon;
      default:
        return Type;
    }
  };

  // Close on outside click (ignore clicks inside nested property popup)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen) return;
      if (nestedPopupRef.current?.contains(event.target)) return;
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, triggerRef]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Get properties based on selected nav (First time is an action in nav, not a tab)
  const getPropertiesForNav = () => {
    if (selectedNavItem === 'all') {
      return [...eventProperties, ...userProperties];
    } else if (selectedNavItem === 'events') {
      return eventProperties;
    } else if (selectedNavItem === 'users') {
      return userProperties;
    }
    return [];
  };

  const filteredProperties = getPropertiesForNav().filter(prop =>
    prop.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prop.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 z-[9998] bg-black bg-opacity-20"
        onClick={onClose}
      />
      {/* Main Property Filter Popup */}
      {createPortal(
        <div
          ref={popupRef}
          className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl flex flex-col overflow-hidden"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            minWidth: '550px',
            maxWidth: '700px',
            maxHeight: '65vh',
            fontFamily: 'Raleway, sans-serif'
          }}
        >
      {/* Search Bar */}
      <div className="px-3 py-2 border-b border-gray-200 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#475569]" />
          <input
            type="text"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2 text-sm rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1d0c46] focus:bg-white border-0"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#475569] hover:text-[#475569]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Main Content: Left Nav + Right Properties */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left Navigation */}
          <div className="w-28 border-r border-gray-200 bg-gray-50 overflow-y-auto flex-shrink-0 min-h-0">
            <div className="py-2">
              {[
                { id: 'all', label: 'All' },
                { id: 'events', label: 'Events' },
                { id: 'users', label: 'Users' },
                { id: 'first_time', label: 'First time', action: true }
              ].map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    if (item.action && item.id === 'first_time') {
                      if (isGlobalFilter && typeof onGlobalFilterAdd === 'function') {
                        onGlobalFilterAdd({
                          id: `global-first-time-${Date.now()}`,
                          category: 'first_time'
                        });
                        onClose();
                        return;
                      }
                      if (typeof onFirstTimeChange === 'function') {
                        onFirstTimeChange(true, eventName, eventId);
                        onClose();
                        return;
                      }
                    }
                    setSelectedNavItem(item.id);
                  }}
                  className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                    selectedNavItem === item.id
                      ? 'bg-white border-l-2 border-[#4b57aa] text-[#4b57aa] font-semibold'
                      : 'text-[#475569] hover:bg-gray-100'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Right Properties Panel - list shows immediately (predefined), custom load in background */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {filteredProperties.length === 0 && !loadingProperties ? (
              <div className="p-4 text-sm text-[#475569] text-center">
                {searchTerm ? 'No properties found matching your search.' : 'No properties available.'}
              </div>
            ) : (
              <div className="p-3">
                {loadingProperties && (
                  <div className="text-xs text-[#475569] text-center py-1 mb-2">Loading custom properties…</div>
                )}
                <div className="space-y-1">
                  {filteredProperties.map((prop, idx) => {
                    const DataTypeIcon = getDataTypeIcon(prop.type);
                    const isSelected = existingFilter && existingFilter.property === prop.name && existingFilter.propertySource === prop.source;
                    return (
                      <button
                        key={`${prop.source}-${prop.name}-${idx}`}
                        onClick={() => handleAddPropertyFilter(prop)}
                        className={`w-full text-left px-2 py-1.5 text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                          isSelected ? 'bg-gray-200 text-[#0f172a] border border-gray-300' : ''
                        }`}
                      >
                        <DataTypeIcon className={`h-4 w-4 flex-shrink-0 ${isSelected ? 'text-[#475569]' : 'text-[#475569]'}`} />
                        <span className={isSelected ? 'text-[#0f172a] font-medium' : 'text-[#0f172a]'}>{prop.label}</span>
                        <span
                          className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-gray-200 text-[#475569] flex-shrink-0"
                          title={prop.source === 'user' ? 'User property' : 'Event property'}
                        >
                          {prop.source === 'user' ? 'U' : 'E'}
                        </span>
                        {isSelected && <span className="ml-auto text-xs text-[#475569]">(Current)</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
        </div>,
        document.body
      )}

      {/* Nested Property Selector Popup (for object properties) */}
      {showNestedSelector && selectedObjectProperty && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[9997]"
            onClick={() => {
              setShowNestedSelector(false);
              setSelectedObjectProperty(null);
            }}
          />
          {/* Nested Property Popup */}
          <div
        ref={nestedPopupRef}
        className="fixed z-[9998] bg-white border border-gray-200 rounded-lg shadow-xl"
        style={{
          top: `${nestedPosition.top}px`,
          left: `${nestedPosition.left}px`,
          minWidth: '300px',
          maxWidth: '400px',
          maxHeight: '400px',
          fontFamily: 'Raleway, sans-serif'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-[#0f172a]">
              Select nested property in "{selectedObjectProperty.label}"
            </h3>
            <button
              onClick={() => {
                setShowNestedSelector(false);
                setSelectedObjectProperty(null);
              }}
              className="text-[#475569] hover:text-[#475569] transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Nested Properties List */}
        <div className="p-2 max-h-80 overflow-y-auto">
          {selectedObjectProperty.nestedProperties && selectedObjectProperty.nestedProperties.length > 0 ? (
            <div className="space-y-1">
              {selectedObjectProperty.nestedProperties.map((nestedProp) => {
                const NestedDataTypeIcon = getDataTypeIcon(nestedProp.type);
                return (
                  <button
                    type="button"
                    key={nestedProp.name}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNestedPropertySelect(nestedProp);
                    }}
                    className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                  >
                    <NestedDataTypeIcon className="h-4 w-4 flex-shrink-0 text-[#475569]" />
                    <span className="text-[#0f172a]">{nestedProp.name}</span>
                    <span className="text-xs text-[#475569] ml-auto">{nestedProp.type}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4 text-[#475569] text-sm">
              No nested properties found
            </div>
          )}
        </div>
          </div>
        </>,
        document.body
      )}
    </>
  );

  function handleAddPropertyFilter(property) {
    // For object properties, always show nested property selector (second popup)
    if (property.type === 'object') {
      setSelectedObjectProperty(property);
      setShowNestedSelector(true);
      if (popupRef.current) {
        const rect = popupRef.current.getBoundingClientRect();
        setNestedPosition({ top: rect.top, left: rect.right + 8 });
      }
      return;
    }

    if (isGlobalFilter && typeof onGlobalFilterAdd === 'function') {
      const newFilter = {
        id: existingFilter?.id || `global-filter-${Date.now()}-${Math.random()}`,
        property: property.name,
        propertyLabel: property.label,
        propertyType: property.type,
        propertySource: property.source,
        sampleValues: property.sampleValues || [],
        operator: existingFilter && property.type === existingFilter.propertyType ? existingFilter.operator : getDefaultOperator(property.type),
        value: existingFilter && property.type === existingFilter.propertyType ? existingFilter.value : null,
        value2: existingFilter && property.type === existingFilter.propertyType ? existingFilter.value2 : null,
        listItemType: property.type === 'list' ? (property.listItemType || 'string') : null,
        listMode: property.type === 'list' ? (existingFilter?.listMode || 'any_in_list') : null,
        category: 'global'
      };
      onGlobalFilterAdd(newFilter);
      onClose();
      return;
    }

    if (existingFilter) {
      // Update existing filter
      const updatedFilter = {
        ...existingFilter,
        property: property.name,
        propertyLabel: property.label,
        propertyType: property.type,
        propertySource: property.source,
        sampleValues: property.sampleValues || [],
        operator: property.type === existingFilter.propertyType ? existingFilter.operator : getDefaultOperator(property.type),
        value: property.type === existingFilter.propertyType ? existingFilter.value : null,
        value2: property.type === existingFilter.propertyType ? existingFilter.value2 : null,
        listItemType: property.type === 'list' ? (property.listItemType || existingFilter.listItemType || 'string') : null,
        listMode: property.type === 'list' ? (existingFilter.listMode || 'any_in_list') : null,
        eventName: existingFilter.eventName || eventName,
        eventId: existingFilter.eventId || eventId
      };
      onFiltersChange(updatedFilter);
    } else {
      const newFilter = {
        id: `property-filter-${Date.now()}-${Math.random()}`,
        property: property.name,
        propertyLabel: property.label,
        propertyType: property.type,
        propertySource: property.source,
        sampleValues: property.sampleValues || [],
        operator: getDefaultOperator(property.type),
        value: null,
        value2: null,
        listItemType: property.type === 'list' ? (property.listItemType || 'string') : null,
        listMode: property.type === 'list' ? 'any_in_list' : null,
        eventName: eventName,
        eventId: eventId
      };
      onFiltersChange(newFilter);
    }
    onClose();
  }

  function handleNestedPropertySelect(nestedProperty) {
    if (!selectedObjectProperty) return;

    // Create the nested property path (e.g., "profile.age")
    const nestedPropertyPath = `${selectedObjectProperty.name}.${nestedProperty.name}`;
    const nestedPropertyLabel = `${selectedObjectProperty.label}.${nestedProperty.name}`;

    if (isGlobalFilter && typeof onGlobalFilterAdd === 'function') {
      const newFilter = {
        id: existingFilter?.id || `global-filter-${Date.now()}-${Math.random()}`,
        property: nestedPropertyPath,
        propertyLabel: nestedPropertyLabel,
        propertyType: nestedProperty.type,
        propertySource: selectedObjectProperty.source,
        sampleValues: nestedProperty.sampleValues || [],
        operator: getDefaultOperator(nestedProperty.type),
        value: null,
        value2: null,
        listItemType: nestedProperty.type === 'list' ? (nestedProperty.listItemType || 'string') : null,
        listMode: nestedProperty.type === 'list' ? 'any_in_list' : null,
        category: 'global'
      };
      onGlobalFilterAdd(newFilter);
      setShowNestedSelector(false);
      setSelectedObjectProperty(null);
      onClose();
      return;
    }

    if (existingFilter) {
      const updatedFilter = {
        ...existingFilter,
        property: nestedPropertyPath,
        propertyLabel: nestedPropertyLabel,
        propertyType: nestedProperty.type,
        propertySource: selectedObjectProperty.source,
        sampleValues: nestedProperty.sampleValues || [],
        operator: getDefaultOperator(nestedProperty.type),
        value: null,
        value2: null,
        listItemType: nestedProperty.type === 'list' ? (nestedProperty.listItemType || 'string') : null,
        listMode: nestedProperty.type === 'list' ? 'any_in_list' : null,
        eventName: existingFilter.eventName || eventName,
        eventId: existingFilter.eventId || eventId
      };
      onFiltersChange(updatedFilter);
    } else {
      const newFilter = {
        id: `property-filter-${Date.now()}-${Math.random()}`,
        property: nestedPropertyPath,
        propertyLabel: nestedPropertyLabel,
        propertyType: nestedProperty.type,
        propertySource: selectedObjectProperty.source,
        sampleValues: nestedProperty.sampleValues || [],
        operator: getDefaultOperator(nestedProperty.type),
        value: null,
        value2: null,
        listItemType: nestedProperty.type === 'list' ? (nestedProperty.listItemType || 'string') : null,
        listMode: nestedProperty.type === 'list' ? 'any_in_list' : null,
        eventName: eventName,
        eventId: eventId
      };
      onFiltersChange(newFilter);
    }

    setShowNestedSelector(false);
    setSelectedObjectProperty(null);
    onClose();
  }


  function handleApplyFilters() {
    if (selectedNavItem === 'first_time' && typeof onFirstTimeChange === 'function') {
      onFirstTimeChange(firstTimeOnlyChecked, eventName, eventId);
    }
    onClose();
  }

  function getDefaultOperator(dataType) {
    switch (dataType) {
      case 'string':
        return 'is';
      case 'number':
        return 'equal';
      case 'boolean':
        return 'is_true';
      case 'datetime':
        return 'last';
      case 'list':
        return 'any_in_list';
      default:
        return 'is';
    }
  }
};

/**
 * Get default operator for a data type
 */
function getDefaultOperatorForType(dataType) {
  switch (dataType) {
    case 'string':
      return 'is';
    case 'number':
      return 'equal';
    case 'boolean':
      return 'is_true';
    case 'datetime':
      return 'equal';
    case 'list':
      return 'any_in_list';
    default:
      return 'is';
  }
}

/**
 * Data Type Selector Component (with Pencil Icon)
 * For list types, allows changing listItemType; for other types, allows changing propertyType
 */
const DataTypeSelector = ({ filter, onUpdate, getDefaultOperatorForType }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  const currentType = filter.propertyType || 'string';
  // For list types, show listItemType options; otherwise show property type options
  const isListType = currentType === 'list';
  const dataTypes = isListType ? [
    { value: 'string', label: 'String' },
    { value: 'number', label: 'Number' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'datetime', label: 'DateTime' }
  ] : [
    { value: 'string', label: 'String' },
    { value: 'number', label: 'Number' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'datetime', label: 'DateTime' },
    { value: 'list', label: 'List' }
  ];

  // Calculate position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      
      setPosition({
        top: rect.bottom + scrollY + 4,
        left: rect.left + scrollX,
        width: Math.max(rect.width, 120)
      });
    }
  }, [isOpen]);

  const getDefaultOp = (type) => {
    switch (type) {
      case 'string': return 'is';
      case 'number': return 'equal';
      case 'boolean': return 'is_true';
      case 'datetime': return 'last';
      default: return 'is';
    }
  };

  const handleTypeChange = (newType) => {
    if (isListType) {
      // Changing listItemType
      onUpdate({ 
        listItemType: newType,
        operator: getDefaultOp(newType),
        value: null,
        value2: null
      });
    } else {
      // Changing propertyType
      onUpdate({ 
        propertyType: newType,
        operator: getDefaultOp(newType),
        value: null,
        value2: null,
        listItemType: newType === 'list' ? 'string' : null,
        listMode: newType === 'list' ? 'any_in_list' : null
      });
    }
    setIsOpen(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-1.5 hover:bg-gray-100 rounded transition-colors"
        title={isListType ? "Change list item type" : "Change data type"}
      >
        <Icon name="edit_pencil" size={18} color="#475569" />
      </button>

      {isOpen && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="fixed z-[9999] bg-white border border-gray-300 rounded shadow-lg"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${position.width}px`,
              minWidth: '120px'
            }}
          >
            <div className="py-1">
              {dataTypes.map((type) => {
                const isSelected = isListType 
                  ? (filter.listItemType || 'string') === type.value
                  : currentType === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => handleTypeChange(type.value)}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${
                      isSelected ? 'bg-gray-200 text-[#0f172a]' : ''
                    }`}
                  >
                    <span>{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

/**
 * Filter Builder Row Component
 * Displays a single filter line with WHERE/AND/OR, property, operator, and value
 */
const FilterBuilderRow = ({ filter, isFirst, onUpdate, onRemove, getDataTypeIcon, teamId, eventName, getDefaultOperatorForType }) => {
  const DataTypeIcon = getDataTypeIcon(filter.propertyType);

  // For list types, operators are based on listItemType.
  // For user datetime properties `first_visit` / `last_visit`, hide some operators.
  let operators = filter.propertyType === 'list'
    ? getOperatorsForType(filter.listItemType || 'string')
    : getOperatorsForType(filter.propertyType);

  if (
    filter.propertyType === 'datetime' &&
    filter.propertySource === 'user' &&
    (filter.property === 'user.first_visit' || filter.property === 'user.last_visit')
  ) {
    const disallowed = new Set([
      'not_in_the_last',
      'not_on',
      'before_the_last',
      'before',
      'in_the_next',
    ]);
    operators = operators.filter(op => !disallowed.has(op.value));
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Connector (WHERE/AND/OR) */}
      {isFirst ? (
        <span className="text-sm font-medium text-[#475569] px-2">WHERE</span>
      ) : (
        <select
          value={filter.connector}
          onChange={(e) => onUpdate({ connector: e.target.value })}
          className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
        >
          <option value="AND">AND</option>
          <option value="OR">OR</option>
        </select>
      )}

      {/* Data Type Icon */}
      <DataTypeIcon className="h-4 w-4 text-[#475569] flex-shrink-0" />

      {/* Property Source Tag: E = Event, U = User */}
      <span
        className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-gray-200 text-[#475569] flex-shrink-0"
        title={filter.propertySource === 'user' ? 'User property' : 'Event property'}
      >
        {filter.propertySource === 'user' ? 'U' : 'E'}
      </span>

      {/* Property Name */}
      <span className="text-sm font-medium text-[#0f172a]">{filter.propertyLabel}</span>

      {/* Operator - For list types, show list mode first, then nested operator */}
      {filter.propertyType === 'list' ? (
        <>
          {/* First: List Mode (Any in list / All in list) */}
          <select
            value={filter.listMode || 'any_in_list'}
            onChange={(e) => {
              const listMode = e.target.value;
              onUpdate({ 
                listMode: listMode,
                operator: getDefaultOperatorForType(filter.listItemType || 'string'), // Reset nested operator
                value: null,
                value2: null
              });
            }}
            className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
          >
            <option value="any_in_list">Any in list</option>
            <option value="all_in_list">All in list</option>
          </select>
          {/* Second: Nested Operator based on listItemType */}
          {(() => {
            const listItemOperators = getOperatorsForType(filter.listItemType || 'string');
            return (
              <select
                value={filter.operator || getDefaultOperatorForType(filter.listItemType || 'string')}
                onChange={(e) => onUpdate({ operator: e.target.value, value: null, value2: null })}
                className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
              >
                {listItemOperators.map(op => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>
            );
          })()}
        </>
      ) : (
        <select
          value={filter.operator}
          onChange={(e) => onUpdate({ operator: e.target.value, value: null })}
          className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
        >
          {operators.map(op => (
            <option key={op.value} value={op.value}>{op.label}</option>
          ))}
        </select>
      )}

      {/* Value Input - always placed on its own line, full width */}
      <div className="w-full mt-1">
        <FilterValueInput
          filter={filter}
          onUpdate={onUpdate}
          teamId={teamId}
          eventName={eventName}
        />
      </div>

      {/* Data Type Selector with Pencil Icon - For list types, allows changing listItemType */}
      <DataTypeSelector 
        filter={filter} 
        onUpdate={onUpdate} 
        getDefaultOperatorForType={getDefaultOperatorForType}
      />

      {/* Delete Button */}
      <button
        onClick={onRemove}
        className="p-1.5 text-[#475569] hover:text-[#475569] transition-colors"
        title="Delete filter"
      >
        <Icon name="delete" size={16} />
      </button>
    </div>
  );
};

/**
 * Multiselect Input Component for Property Values
 */
const PropertyMultiselectInput = ({ value = [], options = [], onChange, placeholder = "Select values..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  const filteredOptions = options.filter(opt =>
    opt.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    opt.value?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleOption = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  // Calculate position when opening with viewport bounds checking
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = 16;

      // Match dropdown width to trigger, but never smaller than 200px
      const dropdownWidth = Math.max(rect.width, 200);
      // Approximate height based on max-h-60 (≈ 15rem)
      const dropdownHeight = 240;

      // Start aligned to the left of the trigger, just below it
      let top = rect.bottom + scrollY + 4;
      let left = rect.left + scrollX;

      // Prevent overflow on the right
      if (left + dropdownWidth > viewportWidth - margin) {
        left = viewportWidth - dropdownWidth - margin;
      }

      // Prevent overflow on the left
      if (left < margin) {
        left = margin;
      }

      // If it would overflow the bottom, flip above the trigger
      if (top + dropdownHeight > viewportHeight + scrollY - margin) {
        top = rect.top + scrollY - dropdownHeight - 4;
      }

      // Ensure we keep a margin from the top
      if (top < scrollY + margin) {
        top = scrollY + margin;
      }

      setPosition({
        top,
        left,
        width: dropdownWidth,
      });
    }
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-2 py-1 text-sm text-left border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46] flex items-center justify-between min-w-[200px]"
      >
        <span className={value.length === 0 ? 'text-[#475569]' : ''}>
          {value.length === 0 ? placeholder : `${value.length} selected`}
        </span>
        <ChevronDown className={`h-4 w-4 text-[#475569] transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {isOpen && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${position.width || 200}px`,
              minWidth: '200px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="py-1">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-[#475569]">No options found</div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = value.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOption(option.value);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                        isSelected ? 'bg-[#4b57aa] text-white' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className={`rounded border-gray-300 focus:ring-[#4b57aa] ${
                          isSelected ? 'text-[#4b57aa]' : ''
                        }`}
                        readOnly
                      />
                      <span>{option.label || option.value}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

/**
 * Filter Value Input Component
 * Renders appropriate input based on operator and data type
 */
const FilterValueInput = ({ filter, onUpdate, teamId, eventName }) => {
  const { operator, propertyType, value, value2, property } = filter;
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Fetch property values for "Is" and "Is Not" operators
  // For list types, check listItemType instead of propertyType
  useEffect(() => {
    const fetchPropertyValues = async () => {
      // Check if this is a string type (either direct string or list with string items)
      const isStringType = propertyType === 'string' || (propertyType === 'list' && (filter.listItemType || 'string') === 'string');
      const shouldFetchValues = isStringType && (operator === 'is' || operator === 'is_not') && property && teamId;

      if (shouldFetchValues) {
        // First check if we have sampleValues from the API (for custom properties)
        if (filter.sampleValues && filter.sampleValues.length > 0) {
          const options = filter.sampleValues
            .map(val => ({ value: val, label: val }));
          setPropertyOptions(options);
          return;
        }

        setLoadingOptions(true);
        try {
          // Get team ID in correct format
          let numericTeamId = null;
          if (typeof teamId === 'object' && teamId !== null) {
            numericTeamId = parseInt(teamId._id || teamId.id, 10);
          } else if (typeof teamId === 'string') {
            try {
              const teamDataStr = localStorage.getItem('selectedTeamData');
              if (teamDataStr) {
                const teamData = JSON.parse(teamDataStr);
                numericTeamId = parseInt(teamData._id || teamData.id, 10);
              } else {
                numericTeamId = parseInt(teamId, 10);
              }
            } catch (e) {
              numericTeamId = parseInt(teamId, 10);
            }
          } else {
            numericTeamId = parseInt(teamId, 10);
          }

          if (isNaN(numericTeamId) || !numericTeamId) {
            setPropertyOptions([]);
            return;
          }

          // Predefined user properties: first/last location city/country, referrers, device/browser/os (from user_identities columns)
          const predefinedUserProps = ['user.first_location_city', 'user.first_location_country', 'user.last_location_city', 'user.last_location_country', 'user.first_referrer', 'user.last_referrer', 'user.device_type', 'user.browser_name', 'user.os'];
          const isPredefinedUserProperty = filter.propertySource === 'user' && predefinedUserProps.includes(property);

          if (isPredefinedUserProperty) {
            const propertyParam = property.replace(/^user\./, '');
            const optsResponse = await axiosInstance.get('/events/user-property-options', {
              params: {
                team_id: numericTeamId,
                property: propertyParam,
                limit: 500
              }
            });
            if (optsResponse.data.success && Array.isArray(optsResponse.data.options)) {
              setPropertyOptions(optsResponse.data.options);
              return;
            }
          }

          // User custom properties (from user_identities.custom_properties JSONB)
          const isUserCustomProperty = filter.propertySource === 'user' && property.startsWith('user.');
          if (isUserCustomProperty) {
            const userCustomPropsResponse = await axiosInstance.get('/events/user-custom-properties', {
              params: {
                team_id: numericTeamId,
                limit: 5000
              }
            });

            if (userCustomPropsResponse.data.success) {
              const propertyName = property.replace(/^user\./, '');
              const foundProp = userCustomPropsResponse.data.custom_properties?.find(p => p.name === propertyName);

              if (foundProp && foundProp.sampleValues && foundProp.sampleValues.length > 0) {
                const options = foundProp.sampleValues
                  .map(val => ({ value: val, label: val }));
                setPropertyOptions(options);
                return;
              }
            }
          }

          // For event custom properties, fetch from custom-properties endpoint
          const isEventCustomProperty = filter.propertySource === 'event' && !['distinct_id', 'country', 'city', 'region', 'timezone', 'browser_name', 'browser_version', 'os', 'os_version', 'device_type', 'language', 'current_url', 'page_title', 'uri', 'initial_referrer', 'referrer', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'element_name', 'element_id', 'element_class', 'element_tag_name', 'element_type', 'element_text', 'viewport_height', 'viewport_width', 'screen_height', 'screen_width', 'dpi', 'is_web3_user', 'wallet_address', 'user_identity'].includes(property) && !property.startsWith('auto_event_data.');
          
          if (isEventCustomProperty && eventName) {
            // Fetch custom properties for this specific event to get sampleValues
            const customPropsResponse = await axiosInstance.get('/events/custom-properties', {
              params: {
                team_id: numericTeamId,
                event_name: eventName,
                limit: 5000
              }
            });
            if (customPropsResponse.data.success) {
              // Find the property in custom_properties or auto_event_data
              let foundProp = null;
              if (property.startsWith('auto_event_data.')) {
                const key = property.replace('auto_event_data.', '');
                foundProp = customPropsResponse.data.auto_event_data?.find(p => p.name === key);
              } else {
                foundProp = customPropsResponse.data.custom_properties?.find(p => p.name === property);
              }

              if (foundProp && foundProp.sampleValues && foundProp.sampleValues.length > 0) {
                const options = foundProp.sampleValues
                  .map(val => ({ value: val, label: val }));
                setPropertyOptions(options);
                return;
              }
            }
          }

          // For predefined properties, fetch from /events. First try with event_name filter
          // so values are scoped to the selected event; if that returns no options, retry
          // without event filter so we show values from all events (fixes empty OS/OS Version dropdowns).
          const predefinedEventProps = ['distinct_id', 'country', 'city', 'region', 'timezone', 'browser_name', 'browser_version', 'os', 'os_version', 'device_type', 'language', 'current_url', 'page_title', 'uri', 'initial_referrer', 'referrer', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'element_name', 'element_id', 'element_class', 'element_tag_name', 'element_type', 'element_text'];
          const isPredefined = predefinedEventProps.includes(property);

          const extractOptionsFromEvents = (events) => {
            const uniqueValues = new Set();
            (events || []).forEach(event => {
              const propValue = event[property];
              if (propValue !== null && propValue !== undefined) {
                const strValue = String(propValue).trim();
                if (strValue) uniqueValues.add(strValue);
              }
            });
            return Array.from(uniqueValues).sort().map(val => ({ value: val, label: val }));
          };

          let filters = [];
          if (eventName) {
            filters.push({ field: 'eventName', operator: 'is', value: [eventName] });
          }

          let response = await axiosInstance.get('/events', {
            params: {
              team_id: numericTeamId,
              limit: 5000,
              ...(filters.length > 0 ? { modularFilters: encodeURIComponent(JSON.stringify(filters)) } : {})
            }
          });

          let options = response.data.success && response.data.events
            ? extractOptionsFromEvents(response.data.events)
            : [];

          // Fallback: for predefined props, if scoped-by-event returned no options, fetch without event filter
          if (isPredefined && options.length === 0 && eventName) {
            response = await axiosInstance.get('/events', {
              params: { team_id: numericTeamId, limit: 5000 }
            });
            if (response.data.success && response.data.events) {
              options = extractOptionsFromEvents(response.data.events);
            }
          }

          setPropertyOptions(options);
        } catch (error) {
          console.error('Error fetching property values:', error);
          setPropertyOptions([]);
        } finally {
          setLoadingOptions(false);
        }
      } else {
        setPropertyOptions([]);
      }
    };

    fetchPropertyValues();
  }, [propertyType, operator, property, teamId, eventName, filter.sampleValues, filter.propertySource, filter.listItemType, filter.listMode]);

  // String operators
  if (propertyType === 'string') {
    if (operator === 'is_set' || operator === 'is_not_set') {
      return null; // No value needed - just checks if property exists
    }
    // For 'is' and 'is_not', show multiselect input with real property values
    if (operator === 'is' || operator === 'is_not') {
      return (
        <PropertyMultiselectInput
          value={Array.isArray(value) ? value : (value ? [value] : [])}
          options={propertyOptions}
          onChange={(newValue) => onUpdate({ value: newValue })}
          placeholder={loadingOptions ? "Loading values..." : "Select values..."}
        />
      );
    }
    // For 'contains' and 'does_not_contain', single text input
    return (
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onUpdate({ value: e.target.value })}
        placeholder="Enter value..."
        className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46] w-32"
      />
    );
  }

  // Number operators
  if (propertyType === 'number') {
    if (operator === 'is_numeric' || operator === 'is_not_numeric') {
      return null;
    }
    if (operator === 'between' || operator === 'not_between') {
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            placeholder="From..."
            className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46] w-24"
          />
          <span className="text-sm text-[#475569]">to</span>
          <input
            type="number"
            value={value2 || ''}
            onChange={(e) => onUpdate({ value2: e.target.value })}
            placeholder="To..."
            className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46] w-24"
          />
        </div>
      );
    }
    return (
      <input
        type="number"
        value={value || ''}
        onChange={(e) => onUpdate({ value: e.target.value })}
        placeholder="Enter value..."
        className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46] w-32"
      />
    );
  }

  // Boolean operators
  if (propertyType === 'boolean') {
    return null; // No value needed for is_true/is_false
  }

  // Date operators
  if (propertyType === 'datetime') {
    if (operator === 'last' || operator === 'not_in_the_last') {
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            placeholder="X"
            className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46] w-16"
          />
          <select
            value={value2 || 'hours'}
            onChange={(e) => onUpdate({ value2: e.target.value })}
            className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
          >
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
            <option value="weeks">weeks</option>
            <option value="months">months</option>
            <option value="years">years</option>
          </select>
          {operator === 'not_in_the_last' && (
            <>
              <span className="text-sm text-[#475569]">ago</span>
            </>
          )}
        </div>
      );
    }
    if (operator === 'between' || operator === 'not_between') {
      return (
        <div className="flex flex-col gap-1 w-full max-w-[180px]">
          <input
            type="datetime-local"
            value={value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            className="w-full max-w-[180px] px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
          />
          <span className="text-xs text-[#475569] text-center">to</span>
          <input
            type="datetime-local"
            value={value2 || ''}
            onChange={(e) => onUpdate({ value2: e.target.value })}
            className="w-full max-w-[180px] px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
          />
        </div>
      );
    }
    if (operator === 'on' || operator === 'not_on' || operator === 'before' || operator === 'since' || operator === 'in_the_next') {
      return (
        <input
          type="datetime-local"
          value={value || ''}
          onChange={(e) => onUpdate({ value: e.target.value })}
          className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
        />
      );
    }
    if (operator === 'before_the_last') {
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            placeholder="X"
            className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46] w-16"
          />
          <select
            value={value2 || 'hours'}
            onChange={(e) => onUpdate({ value2: e.target.value })}
            className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
          >
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
            <option value="weeks">weeks</option>
            <option value="months">months</option>
            <option value="years">years</option>
          </select>
          <span className="text-sm text-[#475569]">ago</span>
        </div>
      );
    }
  }

  // List operators - two-step selection
  if (propertyType === 'list') {
    // List mode is stored in filter.listMode, not filter.value
    // The nested operator is in filter.operator
    // The actual value is in filter.value
    
    // Get list item type (default to string)
    const listItemType = filter.listItemType || 'string';
    const listMode = filter.listMode || 'any_in_list';

    // Render value input based on listItemType and operator
    // Use the same logic as other types but for list items
    if (listItemType === 'string') {
      if (operator === 'is_set' || operator === 'is_not_set') {
        return null; // No value needed
      }
      if (operator === 'is' || operator === 'is_not') {
        // For list items with 'is' operator, show multiselect if we have propertyOptions or sampleValues
        // propertyOptions comes from the parent FilterValueInput's useEffect
        const options = propertyOptions && propertyOptions.length > 0 
          ? propertyOptions 
          : (filter.sampleValues && filter.sampleValues.length > 0 
              ? filter.sampleValues.map(val => ({ value: val, label: val }))
              : []);
        
        if (options.length > 0) {
          return (
            <PropertyMultiselectInput
              value={Array.isArray(value) ? value : (value ? [value] : [])}
              options={options}
              onChange={(newValue) => onUpdate({ value: newValue })}
              placeholder={loadingOptions ? "Loading values..." : "Select values..."}
            />
          );
        }
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            placeholder="Enter value..."
            className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46] w-32"
          />
        );
      }
      // For 'contains' and 'does_not_contain'
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onUpdate({ value: e.target.value })}
          placeholder="Enter value..."
          className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46] w-32"
        />
      );
    }
    
    if (listItemType === 'number') {
      if (operator === 'is_numeric' || operator === 'is_not_numeric') {
        return null;
      }
      if (operator === 'between' || operator === 'not_between') {
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={value || ''}
              onChange={(e) => onUpdate({ value: e.target.value })}
              placeholder="From..."
              className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46] w-24"
            />
            <span className="text-sm text-[#475569]">to</span>
            <input
              type="number"
              value={value2 || ''}
              onChange={(e) => onUpdate({ value2: e.target.value })}
              placeholder="To..."
              className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46] w-24"
            />
          </div>
        );
      }
      return (
        <input
          type="number"
          value={value || ''}
          onChange={(e) => onUpdate({ value: e.target.value })}
          placeholder="Enter value..."
          className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46] w-32"
        />
      );
    }
    
    if (listItemType === 'boolean') {
      return null; // No value needed for is_true/is_false
    }
    
    if (listItemType === 'datetime') {
      if (operator === 'between' || operator === 'not_between') {
        return (
          <div className="flex items-center gap-2">
            <input
              type="datetime-local"
              value={value || ''}
              onChange={(e) => onUpdate({ value: e.target.value })}
              className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
            />
            <span className="text-sm text-[#475569]">to</span>
            <input
              type="datetime-local"
              value={value2 || ''}
              onChange={(e) => onUpdate({ value2: e.target.value })}
              className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
            />
          </div>
        );
      }
      return (
        <input
          type="datetime-local"
          value={value || ''}
          onChange={(e) => onUpdate({ value: e.target.value })}
          className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
        />
      );
    }
    
    // Default to string input
    return (
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onUpdate({ value: e.target.value })}
        placeholder="Enter value..."
        className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1d0c46] w-32"
      />
    );
  }

  return null;
};

/**
 * Get operators for a data type
 */
function getOperatorsForType(dataType) {
  switch (dataType) {
    case 'string':
      return [
        { value: 'is', label: 'Is' },
        { value: 'is_not', label: 'Is not' },
        { value: 'contains', label: 'Contains' },
        { value: 'does_not_contain', label: 'Does not contain' },
        { value: 'is_set', label: 'Is set' },
        { value: 'is_not_set', label: 'Is not set' }
      ];
    case 'number':
      return [
        { value: 'equal', label: 'Equal' },
        { value: 'not_equal', label: 'Not Equal' },
        { value: 'greater_than', label: 'Greater than' },
        { value: 'greater_than_equal', label: 'Greater than equal to' },
        { value: 'less_than', label: 'Less than' },
        { value: 'less_than_equal', label: 'Less than equal to' },
        { value: 'between', label: 'Between' },
        { value: 'not_between', label: 'Not Between' },
        { value: 'is_numeric', label: 'Is numeric' },
        { value: 'is_not_numeric', label: 'Is not numeric' }
      ];
    case 'boolean':
      return [
        { value: 'is_true', label: 'Is true' },
        { value: 'is_false', label: 'Is false' }
      ];
    case 'datetime':
      return [
        { value: 'last', label: 'Last' },
        { value: 'not_in_the_last', label: 'Not in the last' },
        { value: 'between', label: 'Between' },
        { value: 'not_between', label: 'Not between' },
        { value: 'on', label: 'On' },
        { value: 'not_on', label: 'Not on' },
        { value: 'before_the_last', label: 'Before the last' },
        { value: 'before', label: 'Before' },
        { value: 'since', label: 'Since' },
        { value: 'in_the_next', label: 'In the next' }
      ];
    case 'list':
      // List operators are handled separately - this shouldn't be called for list type
      // The actual operators are based on listItemType
      return [];
    default:
      return [];
  }
}

/**
 * Preload property discovery for a team/event so the filter popup opens faster.
 * Call from Events page when events have loaded (e.g. first event name).
 */
export async function preloadPropertyDiscovery(teamId, eventName) {
  if (!teamId || !eventName) return;
  const cacheKey = `${teamId}-${eventName}`;
  if (PROPERTIES_CACHE.has(cacheKey) || RAW_PRELOAD_CACHE.has(cacheKey)) return;

  let numericTeamId = null;
  if (typeof teamId === 'object' && teamId !== null) {
    numericTeamId = parseInt(teamId._id || teamId.id, 10);
  } else if (typeof teamId === 'string') {
    try {
      const teamDataStr = localStorage.getItem('selectedTeamData');
      numericTeamId = teamDataStr ? parseInt(JSON.parse(teamDataStr)._id || JSON.parse(teamDataStr).id, 10) : parseInt(teamId, 10);
    } catch (e) {
      numericTeamId = parseInt(teamId, 10);
    }
  } else {
    numericTeamId = parseInt(teamId, 10);
  }
  if (isNaN(numericTeamId) || !numericTeamId) return;

  try {
    const [customPropsResponse, userCustomPropsResponse] = await Promise.all([
      axiosInstance.get('/events/custom-properties', { params: { team_id: numericTeamId, event_name: eventName, limit: 200 } }),
      axiosInstance.get('/events/user-custom-properties', { params: { team_id: numericTeamId, limit: 300 } })
    ]);
    RAW_PRELOAD_CACHE.set(cacheKey, {
      customPropsResponse,
      userCustomPropsResponse,
      timestamp: Date.now()
    });
  } catch (e) {
    // ignore
  }
}

export default EventPropertyFilterModule;
